import { fetchRetrofitList, getClosestExpirationItem } from '../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { CACHE_TIME } from 'src/Libs/installations/constants'
import { useUserSettings } from 'src/Hooks'
import { useFarmContext } from 'src/Elements/Farm'
import { RetrofitListItem } from '../models'
import { useEffect, useState } from 'react'
import { usePortalQuery } from 'src/Libs/api'

interface UseRetrofitList {
  retrofitList: RetrofitListItem[]
  isRetrofitListLoading: boolean
  isRetrofitListError: boolean
  isServer: boolean
  isRetrofitBannerHidden: boolean
  retrofitTarget: string | undefined
}

export const useRetrofitList = (installationId: string | undefined, serialnumber: string | undefined): UseRetrofitList => {
  const authFetch = useAuthFetchWithResponse()
  const { isFarmMasterSelected, isFarm } = useFarmContext()
  const { userSettings } = useUserSettings()
  const [retrofitTarget, setRetrofitTarget] = useState(serialnumber)

  const fetchRetrofitListQueryFn = async (): Promise<RetrofitListItem[] | null> => {
    const { error, hasErrors, value } = await fetchRetrofitList(installationId, authFetch)
    if (hasErrors) {
      throw error || new Error('An unexpected error occurred while fetching retrofit list')
    }

    return value
  }

  const {
    data: retrofitList,
    isLoading: isRetrofitListLoading,
    isError: isRetrofitListError,
  } = usePortalQuery({
    queryKey: ['retrofit-check-farm', installationId],
    queryFn: fetchRetrofitListQueryFn,
    enabled: !!installationId && isFarmMasterSelected,
    staleTime: CACHE_TIME,
  })

  useEffect(() => {
    if (isFarmMasterSelected && retrofitList) {
      const closestExpiraionItem = getClosestExpirationItem(retrofitList)
      if (closestExpiraionItem?.serialnumber) {
        setRetrofitTarget(closestExpiraionItem?.serialnumber?.toString())
      }
    }
  }, [retrofitList, isFarmMasterSelected])

  /* No children of this farm master have a retrofit option */
  const isFarmMasterWithoutRetrofitOption = !!isFarmMasterSelected && retrofitList?.length === 0

  const isRetrofitBannerHiddenInSettings =
    !installationId || !userSettings?.hideRetrofitBanner ? false : userSettings.hideRetrofitBanner.includes(installationId)

  return {
    retrofitList: retrofitList ?? [],
    isRetrofitListLoading,
    isRetrofitListError,
    isServer: !!isFarmMasterSelected,
    isRetrofitBannerHidden:
      isRetrofitListError || isRetrofitBannerHiddenInSettings || (isFarm && !isFarmMasterSelected) || isFarmMasterWithoutRetrofitOption,
    retrofitTarget,
  }
}
